/**
 * System-to-SKU Mapping Layer
 * Translates project configuration selections into material requirements.
 * Sales users never see raw SKUs — this runs behind the scenes.
 */

import type { ProjectConfiguration, MaterialLineItem } from "./types";
import { getInventoryRecordSync } from "./inventoryAdapter";
import type { RegistryProduct } from "./ProductRegistryContext";

/* ══════════════════════════════════════════════════════════
   Types
   ══════════════════════════════════════════════════════════ */

export type SkuCategory = "base" | "walls" | "glass" | "door" | "fixture" | "accessory" | "trim" | "hardware";

export type VendorType = "Primary" | "Secondary" | "Field";

export interface SkuVendor {
  name: string;
  type: VendorType;
  /** Relative cost indicator: 1 = best, 2 = standard, 3 = expensive */
  costTier: 1 | 2 | 3;
  leadTimeDays: number;
}

export interface SkuItem {
  sku: string;
  itemName: string;
  category: SkuCategory;
  unitCost: number;
  unitPrice?: number;  // optional — list selling price from product registry
  active: boolean;
  primaryVendor: SkuVendor;
  alternateVendor?: SkuVendor;
  /** BoxHero-ready external ID placeholder */
  externalItemId?: string;
}

export interface SkuMappingRule {
  id: string;
  /** Human-readable rule name */
  name: string;
  /** Configuration conditions that trigger this rule */
  conditions: {
    systemType?: string;
    material?: string;
    baseWidth?: string;
    baseLength?: string;
    componentId?: string;
    componentCategory?: string;
  };
  /** SKUs produced when conditions match */
  skuItems: { sku: string; quantity: number }[];
}

export interface SkuBundle {
  id: string;
  name: string;
  description: string;
  skus: string[];
}

export interface MaterialRequirement {
  sku: string;
  itemName: string;
  category: SkuCategory;
  quantityRequired: number;
  unitCost: number;
  unitPrice?: number;  // optional — list selling price from product registry
  /** Simulated inventory fields */
  quantityInStock: number;
  availabilityStatus: "In Stock" | "Partially Available" | "Not Available";
}

export interface MaterialGenerationAudit {
  requirements: MaterialRequirement[];
  missingCatalogSkus: string[];
}

function toVendorName(product: RegistryProduct): string {
  return product.vendor?.trim() || "Product Registry";
}

function toSkuCategory(product: RegistryProduct): SkuCategory {
  switch (product.category) {
    case "Wall System":
      return "walls";
    case "Base":
      return "base";
    case "Glass/Enclosure":
      return product.subcategory?.toLowerCase().includes("door") ? "door" : "glass";
    case "Hardware":
      if (product.subcategory?.toLowerCase().includes("fixture")) return "fixture";
      return "hardware";
    case "Add-On":
      return "accessory";
    default:
      return "hardware";
  }
}

export function buildSkuCatalogFromRegistryProducts(products: RegistryProduct[]): SkuItem[] {
  return products
    .filter((product) => product.active && !!product.sku)
    .map((product) => ({
      sku: product.sku,
      itemName: product.productName,
      category: toSkuCategory(product),
      unitCost: product.cost || 0,
      unitPrice: product.price || 0,
      primaryVendor: {
        name: toVendorName(product),
        type: "Primary" as const,
        costTier: 1,
        leadTimeDays: 0,
      },
    }));
}

export function buildInventoryFromRegistryProducts(products: RegistryProduct[]): Record<string, number> {
  return products.reduce<Record<string, number>>((acc, product) => {
    if (product.active && product.sku) {
      acc[product.sku] = product.stockQuantity || 0;
    }
    return acc;
  }, {});
}

export type MaterialsReadinessStatus = "Ready" | "Partially Available" | "Not Available";

/* ══════════════════════════════════════════════════════════
   Seed SKU Catalog
   ══════════════════════════════════════════════════════════ */

/* ── Vendor Presets ── */
const V_COSENTINO: SkuVendor = { name: "Cosentino", type: "Primary", costTier: 1, leadTimeDays: 5 };
const V_SENTREL: SkuVendor = { name: "Sentrel Direct", type: "Primary", costTier: 1, leadTimeDays: 4 };
const V_TILE_DIST: SkuVendor = { name: "ProBath Distributors", type: "Primary", costTier: 1, leadTimeDays: 5 };
const V_GLASS_DIST: SkuVendor = { name: "National Glass Supply", type: "Primary", costTier: 1, leadTimeDays: 6 };
const V_FIXTURE_DIST: SkuVendor = { name: "National Fixture Warehouse", type: "Primary", costTier: 1, leadTimeDays: 3 };
const V_HARDWARE: SkuVendor = { name: "MainLine Supply Co.", type: "Primary", costTier: 1, leadTimeDays: 2 };
const V_HOME_DEPOT: SkuVendor = { name: "Home Depot", type: "Field", costTier: 3, leadTimeDays: 0 };
const V_LOWES: SkuVendor = { name: "Lowe's", type: "Field", costTier: 3, leadTimeDays: 0 };
const V_BACKUP_GLASS: SkuVendor = { name: "ProBath Distributors", type: "Secondary", costTier: 2, leadTimeDays: 4 };
const V_BACKUP_FIXTURE: SkuVendor = { name: "MainLine Supply Co.", type: "Secondary", costTier: 2, leadTimeDays: 3 };

export const SEED_SKU_CATALOG: SkuItem[] = [
  { sku: "BASE-SEN-3060", itemName: 'Sentrel Base 30"×60"', category: "base", unitCost: 320, unitPrice: 480, primaryVendor: V_SENTREL, alternateVendor: V_HOME_DEPOT },
  { sku: "BASE-SEN-3260", itemName: 'Sentrel Base 32"×60"', category: "base", unitCost: 350, unitPrice: 525, primaryVendor: V_SENTREL, alternateVendor: V_HOME_DEPOT },
  { sku: "BASE-SEN-3648", itemName: 'Sentrel Base 36"×48"', category: "base", unitCost: 340, unitPrice: 510, primaryVendor: V_SENTREL, alternateVendor: V_HOME_DEPOT },
  { sku: "BASE-SEN-3660", itemName: 'Sentrel Base 36"×60"', category: "base", unitCost: 380, unitPrice: 570, primaryVendor: V_SENTREL, alternateVendor: V_HOME_DEPOT },
  { sku: "BASE-SEN-3672", itemName: 'Sentrel Base 36"×72"', category: "base", unitCost: 420, unitPrice: 630, primaryVendor: V_SENTREL, alternateVendor: V_HOME_DEPOT },
  { sku: "BASE-DEK-3060", itemName: 'Dekton Base 30"×60"', category: "base", unitCost: 520, unitPrice: 780, primaryVendor: V_COSENTINO },
  { sku: "BASE-DEK-3660", itemName: 'Dekton Base 36"×60"', category: "base", unitCost: 580, unitPrice: 870, primaryVendor: V_COSENTINO },
  { sku: "BASE-TIL-3060", itemName: 'Tile Base 30"×60"', category: "base", unitCost: 410, unitPrice: 615, primaryVendor: V_TILE_DIST, alternateVendor: V_LOWES },
  { sku: "WALL-SEN-STD", itemName: "Sentrel Standard Wall Panel Set", category: "walls", unitCost: 680, unitPrice: 1020, primaryVendor: V_SENTREL, alternateVendor: V_HOME_DEPOT },
  { sku: "WALL-SEN-PRE", itemName: "Sentrel Premium Wall Panel Set", category: "walls", unitCost: 920, unitPrice: 1380, primaryVendor: V_SENTREL },
  { sku: "WALL-DEK-STD", itemName: "Dekton Wall Panel Set", category: "walls", unitCost: 1450, unitPrice: 2175, primaryVendor: V_COSENTINO },
  { sku: "WALL-TIL-STD", itemName: "Tile Wall Set", category: "walls", unitCost: 1100, unitPrice: 1650, primaryVendor: V_TILE_DIST, alternateVendor: V_LOWES },
  { sku: "GLS-CLR-STD", itemName: "Clear Glass Panel - Standard", category: "glass", unitCost: 280, unitPrice: 420, primaryVendor: V_GLASS_DIST, alternateVendor: V_BACKUP_GLASS },
  { sku: "GLS-FRO-STD", itemName: "Frosted Glass Panel - Standard", category: "glass", unitCost: 320, unitPrice: 480, primaryVendor: V_GLASS_DIST, alternateVendor: V_BACKUP_GLASS },
  { sku: "GLS-CLR-FRM", itemName: "Clear Glass Panel - Framed", category: "glass", unitCost: 220, unitPrice: 330, primaryVendor: V_GLASS_DIST, alternateVendor: V_BACKUP_GLASS },
  { sku: "DOOR-SLD-CHR", itemName: "Sliding Door - Chrome", category: "door", unitCost: 340, unitPrice: 510, primaryVendor: V_GLASS_DIST, alternateVendor: V_HOME_DEPOT },
  { sku: "DOOR-PIV-CHR", itemName: "Pivot Door - Chrome", category: "door", unitCost: 420, unitPrice: 630, primaryVendor: V_GLASS_DIST },
  { sku: "DOOR-SLD-BN", itemName: "Sliding Door - Brushed Nickel", category: "door", unitCost: 380, unitPrice: 570, primaryVendor: V_GLASS_DIST },
  { sku: "FIX-STD-CHR", itemName: "Standard Fixture Pack - Chrome", category: "fixture", unitCost: 180, unitPrice: 270, primaryVendor: V_FIXTURE_DIST, alternateVendor: V_BACKUP_FIXTURE },
  { sku: "FIX-PRE-CHR", itemName: "Premium Fixture Pack - Chrome", category: "fixture", unitCost: 340, unitPrice: 510, primaryVendor: V_FIXTURE_DIST },
  { sku: "FIX-STD-BN", itemName: "Standard Fixture Pack - Brushed Nickel", category: "fixture", unitCost: 220, unitPrice: 330, primaryVendor: V_FIXTURE_DIST, alternateVendor: V_BACKUP_FIXTURE },
  { sku: "ACC-GRAB-18", itemName: 'Grab Bar 18"', category: "accessory", unitCost: 45, unitPrice: 68, primaryVendor: V_HARDWARE, alternateVendor: V_HOME_DEPOT },
  { sku: "ACC-GRAB-24", itemName: 'Grab Bar 24"', category: "accessory", unitCost: 55, unitPrice: 83, primaryVendor: V_HARDWARE, alternateVendor: V_HOME_DEPOT },
  { sku: "ACC-GRAB-36", itemName: 'Grab Bar 36"', category: "accessory", unitCost: 65, unitPrice: 98, primaryVendor: V_HARDWARE, alternateVendor: V_HOME_DEPOT },
  { sku: "ACC-SEAT-FLD", itemName: "Fold-Down Shower Seat", category: "accessory", unitCost: 120, unitPrice: 180, primaryVendor: V_HARDWARE, alternateVendor: V_LOWES },
  { sku: "ACC-SHELF-CRN", itemName: "Corner Shelf", category: "accessory", unitCost: 35, unitPrice: 53, primaryVendor: V_HARDWARE, alternateVendor: V_HOME_DEPOT },
  { sku: "ACC-NICHE-STD", itemName: "Standard Recessed Niche", category: "accessory", unitCost: 85, unitPrice: 128, primaryVendor: V_HARDWARE, alternateVendor: V_HOME_DEPOT },
  { sku: "TRIM-SEAL-KIT", itemName: "Sealant & Trim Kit", category: "trim", unitCost: 65, unitPrice: 98, primaryVendor: V_HARDWARE, alternateVendor: V_HOME_DEPOT },
  { sku: "TRIM-EDGE-CHR", itemName: "Chrome Edge Trim Set", category: "trim", unitCost: 48, unitPrice: 72, primaryVendor: V_HARDWARE, alternateVendor: V_HOME_DEPOT },
  { sku: "HW-MOUNT-KIT", itemName: "Glass Mounting Hardware Kit", category: "hardware", unitCost: 95, unitPrice: 143, primaryVendor: V_HARDWARE, alternateVendor: V_HOME_DEPOT },
  { sku: "HW-DOOR-KIT", itemName: "Door Hardware Kit", category: "hardware", unitCost: 75, unitPrice: 113, primaryVendor: V_HARDWARE, alternateVendor: V_HOME_DEPOT },
];

/* ══════════════════════════════════════════════════════════
   Seed Mapping Rules
   ══════════════════════════════════════════════════════════ */

export const SEED_MAPPING_RULES: SkuMappingRule[] = [
  { id: "rule-base-sen-3060", name: "Sentrel Base 30×60", conditions: { material: "Sentrel", baseWidth: "30", baseLength: "60" }, skuItems: [{ sku: "BASE-SEN-3060", quantity: 1 }] },
  { id: "rule-base-sen-3260", name: "Sentrel Base 32×60", conditions: { material: "Sentrel", baseWidth: "32", baseLength: "60" }, skuItems: [{ sku: "BASE-SEN-3260", quantity: 1 }] },
  { id: "rule-base-sen-3660", name: "Sentrel Base 36×60", conditions: { material: "Sentrel", baseWidth: "36", baseLength: "60" }, skuItems: [{ sku: "BASE-SEN-3660", quantity: 1 }] },
  { id: "rule-base-dek-3060", name: "Dekton Base 30×60", conditions: { material: "Dekton", baseWidth: "30", baseLength: "60" }, skuItems: [{ sku: "BASE-DEK-3060", quantity: 1 }] },
  { id: "rule-base-dek-3660", name: "Dekton Base 36×60", conditions: { material: "Dekton", baseWidth: "36", baseLength: "60" }, skuItems: [{ sku: "BASE-DEK-3660", quantity: 1 }] },
  { id: "rule-base-til-3060", name: "Tile Base 30×60", conditions: { material: "Tile", baseWidth: "30", baseLength: "60" }, skuItems: [{ sku: "BASE-TIL-3060", quantity: 1 }] },
  { id: "rule-wall-sen", name: "Sentrel Walls", conditions: { material: "Sentrel" }, skuItems: [{ sku: "WALL-SEN-STD", quantity: 1 }] },
  { id: "rule-wall-dek", name: "Dekton Walls", conditions: { material: "Dekton" }, skuItems: [{ sku: "WALL-DEK-STD", quantity: 1 }] },
  { id: "rule-wall-til", name: "Tile Walls", conditions: { material: "Tile" }, skuItems: [{ sku: "WALL-TIL-STD", quantity: 1 }] },
  { id: "rule-trim-all", name: "Sealant & Trim Kit", conditions: { systemType: "Walk-in Shower" }, skuItems: [{ sku: "TRIM-SEAL-KIT", quantity: 1 }, { sku: "TRIM-EDGE-CHR", quantity: 1 }] },
  { id: "rule-trim-tub", name: "Tub Sealant Kit", conditions: { systemType: "Standard Tub" }, skuItems: [{ sku: "TRIM-SEAL-KIT", quantity: 1 }] },
  { id: "rule-glass-hw", name: "Glass Hardware", conditions: { componentCategory: "glass" }, skuItems: [{ sku: "HW-MOUNT-KIT", quantity: 1 }] },
  { id: "rule-door-hw", name: "Door Hardware", conditions: { componentCategory: "doors" }, skuItems: [{ sku: "HW-DOOR-KIT", quantity: 1 }] },
];

/* ══════════════════════════════════════════════════════════
   Seed Bundles
   ══════════════════════════════════════════════════════════ */

export const SEED_BUNDLES: SkuBundle[] = [
  { id: "bnd-sen-shower", name: "Sentrel Shower Bundle", description: "Complete Sentrel walk-in shower package", skus: ["BASE-SEN-3060", "WALL-SEN-STD", "GLS-CLR-STD", "DOOR-SLD-CHR", "FIX-STD-CHR", "TRIM-SEAL-KIT", "TRIM-EDGE-CHR", "HW-MOUNT-KIT", "HW-DOOR-KIT"] },
  { id: "bnd-dek-shower", name: "Dekton Shower Bundle", description: "Premium Dekton walk-in shower package", skus: ["BASE-DEK-3060", "WALL-DEK-STD", "GLS-FRO-STD", "DOOR-PIV-CHR", "FIX-PRE-CHR", "TRIM-SEAL-KIT", "TRIM-EDGE-CHR", "HW-MOUNT-KIT", "HW-DOOR-KIT"] },
];

/* ══════════════════════════════════════════════════════════
   Simulated Inventory (legacy adapter-backed accessor)
   ══════════════════════════════════════════════════════════ */

/** @deprecated Use inventory adapter access directly where possible */
export function getSimulatedInventory(): Record<string, number> {
  return getInventoryRecordSync();
}

/* ══════════════════════════════════════════════════════════
   Material List Generation Engine
   ══════════════════════════════════════════════════════════ */

function matchesCondition(rule: SkuMappingRule, config: ProjectConfiguration, productOptionIds: string[]): boolean {
  const c = rule.conditions;
  if (c.systemType && config.bathroomSystemType !== c.systemType) return false;
  if (c.material && !config.bathroomMaterials.includes(c.material as any)) return false;
  if (c.baseWidth && config.baseWidth !== c.baseWidth) return false;
  if (c.baseLength && config.baseLength !== c.baseLength) return false;
  if (c.componentId && !productOptionIds.includes(c.componentId)) return false;
  if (c.componentCategory) {
    const catMap: Record<string, string[]> = {
      glass: [config.selectedGlass].filter(Boolean),
      doors: [config.selectedDoor].filter(Boolean),
      fixtures: [config.selectedFixture].filter(Boolean),
      "grab-bars": config.selectedGrabBars,
      "add-ons": config.selectedAddOns,
    };
    const ids = catMap[c.componentCategory] || [];
    if (ids.length === 0) return false;
  }
  return true;
}

export function generateMaterialList(
  config: ProjectConfiguration,
  rules: SkuMappingRule[] = SEED_MAPPING_RULES,
  catalog: SkuItem[] = SEED_SKU_CATALOG,
  inventory?: Record<string, number>,
): MaterialRequirement[] {
  const inv = inventory ?? getInventoryRecordSync();
  const optionIds = [
    config.selectedWallSystem,
    config.selectedBase,
    config.selectedGlass,
    config.selectedDoor,
    config.selectedFixture,
    ...config.selectedGrabBars,
    ...config.selectedAddOns,
  ].filter(Boolean);

  const matchedSkus = new Map<string, number>();
  for (const rule of rules) {
    if (matchesCondition(rule, config, optionIds)) {
      for (const si of rule.skuItems) {
        matchedSkus.set(si.sku, (matchedSkus.get(si.sku) || 0) + si.quantity);
      }
    }
  }

  const requirements: MaterialRequirement[] = [];
  for (const [sku, qty] of matchedSkus) {
    const item = catalog.find((c) => c.sku === sku);
    if (!item) continue;
    const inStock = inv[sku] ?? 0;
    let status: MaterialRequirement["availabilityStatus"] = "Not Available";
    if (inStock >= qty) status = "In Stock";
    else if (inStock > 0) status = "Partially Available";
    requirements.push({
      sku,
      itemName: item.itemName,
      category: item.category,
      quantityRequired: qty,
      unitCost: item.unitCost,
      unitPrice: item.unitPrice,
      quantityInStock: inStock,
      availabilityStatus: status,
    });
  }

  const catOrder: SkuCategory[] = ["base", "walls", "glass", "door", "fixture", "accessory", "trim", "hardware"];
  requirements.sort((a, b) => catOrder.indexOf(a.category) - catOrder.indexOf(b.category));

  return requirements;
}

export function generateMaterialListAudit(
  config: ProjectConfiguration,
  rules: SkuMappingRule[] = SEED_MAPPING_RULES,
  catalog: SkuItem[] = SEED_SKU_CATALOG,
  inventory?: Record<string, number>,
): MaterialGenerationAudit {
  const inv = inventory ?? getInventoryRecordSync();
  const optionIds = [
    config.selectedWallSystem,
    config.selectedBase,
    config.selectedGlass,
    config.selectedDoor,
    config.selectedFixture,
    ...config.selectedGrabBars,
    ...config.selectedAddOns,
  ].filter(Boolean);

  const matchedSkus = new Map<string, number>();
  for (const rule of rules) {
    if (matchesCondition(rule, config, optionIds)) {
      for (const si of rule.skuItems) {
        matchedSkus.set(si.sku, (matchedSkus.get(si.sku) || 0) + si.quantity);
      }
    }
  }

  const missingCatalogSkus: string[] = [];
  const requirements: MaterialRequirement[] = [];
  for (const [sku, qty] of matchedSkus) {
    const item = catalog.find((c) => c.sku === sku);
    if (!item) {
      missingCatalogSkus.push(sku);
      continue;
    }
    const inStock = inv[sku] ?? 0;
    let status: MaterialRequirement["availabilityStatus"] = "Not Available";
    if (inStock >= qty) status = "In Stock";
    else if (inStock > 0) status = "Partially Available";
    requirements.push({
      sku,
      itemName: item.itemName,
      category: item.category,
      quantityRequired: qty,
      unitCost: item.unitCost,
      unitPrice: item.unitPrice,
      quantityInStock: inStock,
      availabilityStatus: status,
    });
  }

  const catOrder: SkuCategory[] = ["base", "walls", "glass", "door", "fixture", "accessory", "trim", "hardware"];
  requirements.sort((a, b) => catOrder.indexOf(a.category) - catOrder.indexOf(b.category));

  return { requirements, missingCatalogSkus };
}

export function computeMaterialsReadiness(requirements: MaterialRequirement[]): MaterialsReadinessStatus {
  if (requirements.length === 0) return "Not Available";
  if (requirements.every((r) => r.availabilityStatus === "In Stock")) return "Ready";
  if (requirements.some((r) => r.availabilityStatus === "In Stock" || r.availabilityStatus === "Partially Available")) return "Partially Available";
  return "Not Available";
}

export function toMaterialLineItems(requirements: MaterialRequirement[]): MaterialLineItem[] {
  return requirements.map((r) => ({
    sku: r.sku,
    itemName: r.itemName,
    quantityRequired: r.quantityRequired,
    quantityAllocated: r.availabilityStatus === "In Stock" ? r.quantityRequired : r.availabilityStatus === "Partially Available" ? r.quantityInStock : 0,
    quantityStaged: 0,
    quantityInstalled: 0,
    missingItems: Math.max(0, r.quantityRequired - (r.availabilityStatus === "In Stock" ? r.quantityRequired : r.quantityInStock)),
    unitCost: r.unitCost,
    unitPrice: r.unitPrice,
    fieldPurchase: false,
    inventoryAssignment: "Stock" as const,
    lifecycleStage: r.availabilityStatus === "In Stock" ? "Received" as const : "Ordered" as const,
    inventoryStatus: r.availabilityStatus === "In Stock" ? "In Stock" : r.availabilityStatus === "Partially Available" ? "Low Stock" : "Allocated",
  }));
}

/* ══════════════════════════════════════════════════════════
   Vendor Routing Engine
   ══════════════════════════════════════════════════════════ */

export type OrderType = "batch" | "emergency";

export interface VendorRouting {
  vendor: SkuVendor;
  costTier: 1 | 2 | 3;
  /** "best" | "standard" | "expensive" */
  costLabel: string;
  reason: string;
}

const COST_LABELS: Record<1 | 2 | 3, string> = { 1: "best", 2: "standard", 3: "expensive" };

/**
 * Determine the best vendor for a SKU given the order type.
 * Batch → Primary. Emergency → fastest (field vendor if available).
 * If inventory is sufficient, returns null (no order needed).
 */
export function routeVendor(
  sku: SkuItem,
  orderType: OrderType,
  inventoryAvailable?: number,
  quantityNeeded?: number,
): VendorRouting | null {
  // Skip ordering if inventory covers it
  if (inventoryAvailable !== undefined && quantityNeeded !== undefined && inventoryAvailable >= quantityNeeded) {
    return null;
  }

  if (orderType === "batch") {
    return {
      vendor: sku.primaryVendor,
      costTier: sku.primaryVendor.costTier,
      costLabel: COST_LABELS[sku.primaryVendor.costTier],
      reason: "Primary vendor — best price",
    };
  }

  // Emergency: prefer fastest (field vendor if available, else alternate, else primary)
  const fieldVendor = [sku.alternateVendor, sku.primaryVendor].find(v => v?.type === "Field");
  if (fieldVendor) {
    return {
      vendor: fieldVendor,
      costTier: fieldVendor.costTier,
      costLabel: COST_LABELS[fieldVendor.costTier],
      reason: `${fieldVendor.name} (faster, higher cost)`,
    };
  }

  // Fastest by lead time
  const candidates = [sku.primaryVendor, sku.alternateVendor].filter(Boolean) as SkuVendor[];
  const fastest = candidates.sort((a, b) => a.leadTimeDays - b.leadTimeDays)[0];
  return {
    vendor: fastest,
    costTier: fastest.costTier,
    costLabel: COST_LABELS[fastest.costTier],
    reason: fastest.type === "Primary" ? "Primary vendor" : `${fastest.name} (faster availability)`,
  };
}

/**
 * Resolve the primary vendor name for a material string (used in Warehouse Command Center).
 */
export function resolveVendorForMaterial(material: string, catalog: SkuItem[] = SEED_SKU_CATALOG): SkuVendor | null {
  const matLower = material.toLowerCase();
  // Find first SKU matching this material family
  const match = catalog.find(s => {
    if (matLower.includes("dekton")) return s.sku.includes("DEK");
    if (matLower.includes("sentrel")) return s.sku.includes("SEN");
    if (matLower.includes("tile")) return s.sku.includes("TIL");
    if (matLower.includes("pgt") || matLower.includes("winguard")) return s.category === "glass";
    return false;
  });
   return match?.primaryVendor ?? null;
}

/* ══════════════════════════════════════════════════════════
   Material Delta Computation (for quote/config changes)
   ══════════════════════════════════════════════════════════ */

export interface MaterialDelta {
  sku: string;
  name: string;
  deltaAmount: number;  // positive = increase, negative = decrease
  oldQty: number;
  newQty: number;
  oldUnitPrice: number;
  newUnitPrice: number;
}

/**
 * Compute cost deltas between two project configurations.
 * Compares material lists derived from baseline vs current configs.
 */
export function computeMaterialDeltas(
  baselineConfig: ProjectConfiguration,
  currentConfig: ProjectConfiguration,
  skuCatalog: SkuItem[],
  registryProducts: RegistryProduct[],
  mappingRules: SkuMappingRule[] = SEED_MAPPING_RULES
): MaterialDelta[] {
  // Build SKU catalog from registry products if not provided
  const effectiveCatalog = skuCatalog.length > 0 ? skuCatalog : buildSkuCatalogFromRegistryProducts(registryProducts);
  const inventory: Record<string, number> = {};

  // Derive material requirements for both configs
  const baselineReqs = generateMaterialList(baselineConfig, mappingRules, effectiveCatalog, inventory);
  const currentReqs = generateMaterialList(currentConfig, mappingRules, effectiveCatalog, inventory);

  // Build lookup maps by SKU
  const baselineBySku = new Map<string, MaterialRequirement & { unitPrice?: number }>();
  baselineReqs.forEach(r => baselineBySku.set(r.sku, r));

  const currentBySku = new Map<string, MaterialRequirement & { unitPrice?: number }>();
  currentReqs.forEach(r => currentBySku.set(r.sku, r));

  const deltas: MaterialDelta[] = [];

  // Process all unique SKUs from both sets
  const allSkus = new Set([...baselineBySku.keys(), ...currentBySku.keys()]);

  for (const sku of allSkus) {
    const baseline = baselineBySku.get(sku);
    const current = currentBySku.get(sku);

    if (baseline && current) {
      // Item exists in both — compute change
      const oldTotal = (baseline.quantityRequired || 0) * (baseline.unitPrice || 0);
      const newTotal = (current.quantityRequired || 0) * (current.unitPrice || 0);
      const delta = newTotal - oldTotal;
      if (delta !== 0) {
        deltas.push({
          sku,
          name: current.itemName,
          deltaAmount: delta,
          oldQty: baseline.quantityRequired,
          newQty: current.quantityRequired,
          oldUnitPrice: baseline.unitPrice || 0,
          newUnitPrice: current.unitPrice || 0,
        });
      }
    } else if (baseline && !current) {
      // Removed
      const oldTotal = (baseline.quantityRequired || 0) * (baseline.unitPrice || 0);
      if (oldTotal > 0) {
        deltas.push({
          sku,
          name: baseline.itemName,
          deltaAmount: -oldTotal,
          oldQty: baseline.quantityRequired,
          newQty: 0,
          oldUnitPrice: baseline.unitPrice || 0,
          newUnitPrice: 0,
        });
      }
    } else if (!baseline && current) {
      // Added
      const newTotal = (current.quantityRequired || 0) * (current.unitPrice || 0);
      if (newTotal > 0) {
        deltas.push({
          sku,
          name: current.itemName,
          deltaAmount: newTotal,
          oldQty: 0,
          newQty: current.quantityRequired,
          oldUnitPrice: 0,
          newUnitPrice: current.unitPrice || 0,
        });
      }
    }
  }

  return deltas;
}
